import React from 'react';

import { schemas } from '@recurrency/core-api-schema';
import { ItemAlternateSupplierDTO } from '@recurrency/core-api-schema/dist/v2/items/getItemAlternateSuppliers';
import { ColumnType } from 'antd/lib/table';

import { PurchasingVendorLink } from 'components/Links';
import { CenteredError } from 'components/Loaders';
import { DEFAULT_NUM_ROWS_SHOWN, MiniTable } from 'components/MiniTable';
import { ViewAllModal } from 'components/recipes/sidePane/ViewAllModal';
import { Table } from 'components/Table';

import { useCoreApi } from 'hooks/useApi';

import { showAsyncModal } from 'utils/asyncModal';
import { formatUSD, joinIdNameObj } from 'utils/formatting';
import { typedColumn } from 'utils/tables';

import { formatLastPurchaseDate } from './utils';

const getSupplierName = (record: ItemAlternateSupplierDTO) => record.supplierName ?? record.supplierId;

export function AlternateSuppliersPanelContent({ itemUid, locationId }: { itemUid: string; locationId: string }) {
  const { data, isLoading, error } = useCoreApi(schemas.items.getItemAlternateSuppliersV2, {
    pathParams: { itemUid },
    queryParams: { locationId, limit: DEFAULT_NUM_ROWS_SHOWN },
  });

  return (
    <MiniTable
      numRowsShown={DEFAULT_NUM_ROWS_SHOWN}
      data={data?.items}
      columns={[
        { render: getSupplierName, maxWidth: '130px' },
        { render: (record) => formatLastPurchaseDate(record.lastPurchaseDate), align: 'left' },
        { render: (record) => formatUSD(record.lastPurchaseUnitPrice, true), align: 'right' },
      ]}
      isLoading={isLoading}
      error={error}
      onMoreButtonClick={() => {
        showAsyncModal(ViewAllModal, {
          title: 'Alternate Suppliers',
          content: <AlternateSuppliersTable itemUid={itemUid} locationId={locationId} />,
        });
      }}
    />
  );
}

function AlternateSuppliersTable({ itemUid, locationId }: { itemUid: string; locationId: string }) {
  // no limit: the modal shows every alternate supplier
  const { data, isLoading, error } = useCoreApi(schemas.items.getItemAlternateSuppliersV2, {
    pathParams: { itemUid },
    queryParams: { locationId },
  });

  const columns: ColumnType<ItemAlternateSupplierDTO>[] = [
    typedColumn({
      key: 'supplier',
      title: 'Supplier',
      render: (_, record) => joinIdNameObj({ foreignId: record.supplierId, name: record.supplierName ?? '' }),
    }),
    typedColumn({
      key: 'vendor',
      title: 'Vendor',
      render: (_, record) =>
        record.vendorId ? (
          <PurchasingVendorLink vendorId={record.vendorId}>
            {joinIdNameObj({ foreignId: record.vendorId, name: record.vendorName ?? '' })}
          </PurchasingVendorLink>
        ) : (
          '-'
        ),
    }),
    typedColumn({
      key: 'lastPurchaseDate',
      title: 'Last Purchased',
      render: (_, record) => formatLastPurchaseDate(record.lastPurchaseDate),
    }),
    typedColumn({
      key: 'purchaseOrderLineCount',
      title: 'PO Line Count',
      align: 'right' as const,
      render: (_, record) => record.purchaseOrderLineCount,
    }),
    typedColumn({
      key: 'lastPurchaseUnitPrice',
      title: 'Last Unit Price',
      align: 'right' as const,
      render: (_, record) => formatUSD(record.lastPurchaseUnitPrice, true),
    }),
  ];

  if (error) {
    return <CenteredError error={error} />;
  }

  return (
    <Table
      rowKey={(record: ItemAlternateSupplierDTO) => `${record.companyId}:${record.supplierId}`}
      isLoading={isLoading}
      data={data?.items ?? []}
      columns={columns}
    />
  );
}
